export type FaqUiCategory = "about" | "delivery" | "ai-human" | "commercial";

export const faqCategories: Array<{
  key: FaqUiCategory;
  label: string;
  accent: string;
  rgb: string;
}> = [
  { key: "about", label: "About FTO", accent: "#6fcfff", rgb: "111,207,255" },
  { key: "delivery", label: "Delivery & Operations", accent: "#00b879", rgb: "0,184,121" },
  { key: "ai-human", label: "AI + Human", accent: "#f6921e", rgb: "246,146,30" },
  { key: "commercial", label: "Commercial", accent: "#8e8ca5", rgb: "142,140,165" },
];

export const faqs: Array<{
  category: FaqUiCategory;
  question: string;
  answer: string;
  related: Array<{ label: string; chapter: string }>;
}> = [
  {
    category: "about",
    question: "What does FTO do?",
    answer: "FTO helps companies build and scale high-performing teams for customer experience, sales development, back-office operations, and specialized industry workflows. We combine skilled people, technology, operating discipline, and accountability to deliver measurable outcomes.",
    related: [
      { label: "EXPLORE SERVICES & WORKFLOWS", chapter: "solutions" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "about",
    question: "How is FTO different from a traditional call center?",
    answer: "Traditional call centers often optimize for activity and volume. FTO designs the operating model around the experience and outcomes your brand needs to protect, combining trained people, technology, quality discipline, and continuous improvement.",
    related: [
      { label: "Explore The Delivery Standard", chapter: "signal" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "about",
    question: "What services and workflows can FTO support?",
    answer: "FTO can support customer experience, sales development, back-office operations, insurance and healthcare processes, technical and administrative support, and other specialized workflows. The final scope is designed around your systems, volume, risk, and success measures.",
    related: [
      { label: "Explore Live Workflow", chapter: "workflow" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "about",
    question: "Which industries does FTO support?",
    answer: "FTO supports Insurance, Healthcare, Financial Services, Technology, Consumer Goods, and Education. Delivery is adapted to each industry’s customer expectations, workflow demands, and operating realities.",
    related: [
      { label: "View Industries", chapter: "industries" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "delivery",
    question: "Where does FTO provide outsourcing services?",
    answer: "FTO provides outsourcing services through delivery operations in the United States, the Philippines, and Ghana, supporting programs across markets and time zones.",
    related: [
      { label: "Explore Global Delivery", chapter: "standard" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "delivery",
    question: "How does outsourcing with FTO work?",
    answer: "We define the workflow and success measures, build and launch the team, then manage performance through training, quality assurance, reporting, and continuous improvement.",
    related: [
      { label: "Explore Live Workflow", chapter: "workflow" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "delivery",
    question: "Can FTO teams work within our existing systems and workflows?",
    answer: "Yes. FTO teams can work within approved client systems, tools, workflows, and reporting environments. Access, training, documentation, and operating responsibilities are defined during solution design and onboarding.",
    related: [
      { label: "EXPLORE LIVE WORKFLOW", chapter: "workflow" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "delivery",
    question: "How does FTO maintain quality and brand consistency?",
    answer: "FTO translates the client’s brand and operating standards into training, documented workflows, quality assurance, coaching, reporting, and continuous improvement. Performance is reviewed against the measures agreed for each program.",
    related: [
      { label: "EXPLORE OUTCOMES", chapter: "outcomes" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "ai-human",
    question: "How does FTO combine AI and people?",
    answer: "FTO uses AI where speed, pattern recognition, and repeatability improve the workflow, and people where empathy, nuance, judgment, and accountability matter. The operating model assigns each task to the right form of execution.",
    related: [
      { label: "EXPLORE AI + HUMAN", chapter: "perspectives" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "ai-human",
    question: "When should AI lead, and when should human judgment lead?",
    answer: "AI should lead in high-volume, repeatable, data-rich work with clear rules. Human judgment should lead when context changes, trust is at risk, exceptions require interpretation, or someone must own the outcome. Many workflows use both.",
    related: [
      { label: "EXPLORE AI + HUMAN", chapter: "perspectives" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "commercial",
    question: "How quickly can FTO launch and scale a team?",
    answer: "Launch timing depends on the scope, role complexity, hiring needs, systems, training, and compliance requirements. FTO defines a practical launch plan during discovery and can scale in controlled phases as performance stabilizes.",
    related: [
      { label: "EXPLORE LIVE WORKFLOW", chapter: "workflow" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "commercial",
    question: "How does FTO protect data and support compliance requirements?",
    answer: "FTO aligns access controls, documentation, training, quality processes, and reporting with the client’s security and compliance requirements. Specific responsibilities, controls, and evidence are defined during solution design before launch.",
    related: [
      { label: "View Industries", chapter: "industries" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
  {
    category: "commercial",
    question: "How does FTO measure performance and report results?",
    answer: "FTO defines program-specific KPIs, quality standards, service levels, and reporting with the client. Performance is reviewed through operational reporting, quality monitoring, coaching, and continuous improvement against agreed outcomes.",
    related: [
      { label: "EXPLORE OUTCOMES", chapter: "outcomes" },
      { label: "START THE CONVERSATION", chapter: "contact" },
    ],
  },
];

export const faqPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: {
      "@type": "Answer",
      text: answer,
    },
  })),
};
